import React from 'react';
import { Select } from 'semantic-ui-react';
import styled from 'styled-components';
import { areasOf, districtsOf, findAreaById, townsOf, useDeliveryAreas } from '../../utils/deliveryAreas';

/** What the pickers hand back: the picked khoroo's id (null until a khoroo is picked) and the text picked so far. */
export interface AreaSelection {
    areaId: number | null
    town: string
    district: string
    unit: string
}

interface AreaFieldsProps {
    /** the picked khoroo; the address is saved by this id, never by its text */
    areaId: number | null | undefined
    /** the town / district picked so far (a form keeps them while the khoroo itself is still to be picked) */
    town: string
    district: string
    onChange: (selection: AreaSelection) => void
    /** data-testid prefix: "<prefix>-town", "<prefix>-district", "<prefix>-unit" */
    testIdPrefix: string
    disabled?: boolean
    /** Free-text address saved before the list existed; shown for reference while the user picks from the lists. */
    legacy?: { town: string, district: string, unit: string } | null
}

const Fields = styled.div`
    display: flex;
    flex-wrap: wrap;
    gap: 16px;
    width: 100%;
`;

const Field = styled.div`
    flex: 1 1 180px;
    min-width: 0;
    text-align: left;

    label {
        display: block;
        font-weight: 700;
        margin-bottom: 6px;
    }
    label::after {
        content: ' *';
        color: #db2828;
    }
    .ui.selection.dropdown {
        width: 100%;
        min-width: 0;
    }
`;

const Notice = styled.div`
    width: 100%;
    font-size: 13px;
    color: #767676;
`;

const ErrorNotice = styled(Notice)`
    color: #db2828;
`;

const toOptions = (values: string[]) => values.map((v) => ({ key: v, value: v, text: v }));

/**
 * Town / district / khoroo pickers backed by the delivery areas list. Picking a higher level clears the lower
 * ones, and the khoroo (the last picker) is what identifies the address: its id goes to the backend. Text that is
 * not in the list (a free-text address from before the list) shows as empty so it has to be picked again.
 */
function AreaFields(props: AreaFieldsProps) {
    const { areas, loading, error } = useDeliveryAreas();
    const { disabled, legacy } = props;

    const selected = findAreaById(areas, props.areaId);
    const towns = townsOf(areas);
    const town = selected ? selected.town : (towns.includes(props.town) ? props.town : '');
    const districts = districtsOf(areas, town);
    const district = selected ? selected.district : (districts.includes(props.district) ? props.district : '');
    const khoroo = areasOf(areas, town, district);

    const showLegacy = Boolean(legacy && (legacy.district || legacy.unit)) && !loading && !error;

    return (
        <Fields>
            {showLegacy ? (
                <Notice data-testid={`${props.testIdPrefix}-legacy`}>
                    Хуучин хаяг: {[legacy!.town, legacy!.district, legacy!.unit].filter(Boolean).join(', ')}. Доороос шинээр сонгоно уу.
                </Notice>
            ) : null}
            {error ? <ErrorNotice>Хаягийн жагсаалтыг ачаалж чадсангүй. Хуудсаа дахин ачаална уу.</ErrorNotice> : null}
            <Field>
                <label>Хот</label>
                <Select
                    data-testid={`${props.testIdPrefix}-town`}
                    fluid
                    search
                    placeholder="Сонгох"
                    loading={loading}
                    disabled={disabled || loading || error}
                    options={toOptions(towns)}
                    value={town}
                    onChange={(e, data) => {
                        if (data.value !== town) props.onChange({ areaId: null, town: String(data.value), district: '', unit: '' });
                    }}
                />
            </Field>
            <Field>
                <label>Дүүрэг</label>
                <Select
                    data-testid={`${props.testIdPrefix}-district`}
                    fluid
                    search
                    placeholder="Сонгох"
                    loading={loading}
                    disabled={disabled || loading || error || !town}
                    options={toOptions(districts)}
                    value={district}
                    onChange={(e, data) => {
                        if (data.value !== district) props.onChange({ areaId: null, town, district: String(data.value), unit: '' });
                    }}
                />
            </Field>
            <Field>
                <label>Хороо</label>
                <Select
                    data-testid={`${props.testIdPrefix}-unit`}
                    fluid
                    search
                    placeholder="Сонгох"
                    loading={loading}
                    disabled={disabled || loading || error || !district}
                    options={khoroo.map((a) => ({ key: a.id, value: a.id, text: a.unit }))}
                    value={selected ? selected.id : ''}
                    onChange={(e, data) => {
                        const picked = findAreaById(areas, Number(data.value));
                        if (picked) props.onChange({ areaId: picked.id, town: picked.town, district: picked.district, unit: picked.unit });
                    }}
                />
            </Field>
        </Fields>
    );
}

export default AreaFields;
